import React, { useEffect, useState, useContext, useRef } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { useNetworkIdRedirect } from "./common/hooks";
import { Container, Row, Col, Button, Form, Modal, FormControl } from "react-bootstrap";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import refreshIcon from "assets/svg/refresh-green.svg";
import { IconButton } from "@mui/material";
import { DownloadDataUI } from "components/gateways/DownloadDataUI";
import {
    formatValueWithUnit,
    customMinMax,
    formatUnitWithSymbol,
    formatThresholdValue,
    handleAccuracyException,
    getExactAccuracy,
    isHttpSuccess,
    getPrimaryUnitName,
} from "utils/functions";
import {
    decimalPlaceMatches,
    extractedIdList,
} from "utils/gatewayFunctions";
import { formatSensorReadingCellValue } from "./common/sensorValueFormatter";
import { getCalendarFormat } from "utils/datetime";
import moment from "moment";
import { AppContext } from "context/appContext";
import ripple from "assets/img/ripple.gif";
import FormDeviceValueControl from "components/form-control/FormDeviceValueControl";
import AlertNoticeConfig, { getDefaultAlertConfig } from "components/gateways/AlertNoticeConfig";
import DefaultModal from "components/modals/ModalTemplate";
import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import {
    AlertPeriod,
    Operator,
    Patterns,
    SensorAlertMode,
    SensorDataType,
    SensorDefaultMessage,
    SensorThreshold,
    HttpStatus,
} from "constant";
import { AlertConfig, MemberDto, ResponseDto } from "types";
import AxiosInstance from "service/axiosInstance";
import { useOrganisation } from "hooks/organization";
import {
    getOneRegistry,
    getSensorLastReading,
    createNewAlertNotification,
    updateAlertNotification,
    updateSensorNotice,
    updateSensorConfig,
} from "service/gatewayService";
import LocalStorageService from "service/localStorageService";
import { get } from "lodash";
import { getAPIError, showErrorAlert, showSuccessAlert, showInfoAlert } from "utils/alert";
import { WalletAlertComponent, useWalletStore } from "hooks/wallet";
import { AlertNoticePatch } from "generated/models/Dashboard";
import { BREADCRUMB_LABELS, DEFAULT_VALUES, STYLE_CONSTANTS } from "./constants";
import { getTagDeviceUID, getTagDeviceSAID } from "./common/deviceUtils";
import { DynamicBreadcrumb } from "./common/DynamicBreadcrumb";
import { runSensorDetailsMountEffect } from "./sensorDetailsMountEffect";

const { PAYMENT_REQUIRED } = HttpStatus;

type SensorReading = {
    value: number | null;
    lowest: number | null;
    highest: number | null;
    timestamp: number | null;
};

type Sensor = {
    name: string;
    type: string;
    sensor_type: string;
    currentValue: number;
    minValue: number;
    maxValue: number;
    unit: string;
    accuracy: number;
    format?: string;
    lastActive: string;
    ldsuName: string;
    ldsuUuid: string;
    SAID?: number;
    hubName: string;
    hubPort: string;
    networkName: string;
    roomMembership: string;
    groupsMembership: string[];
    readings: SensorReading;
    originalData?: any;
};

// Debug helper function to analyze sensor data structure
const debugSensorData = (sensorData: {
    UID: string;
    ts: number;
    value: number[];
    min: number[];
    max: number[];
}, sensorSAID?: number) => {
    const numSensors = sensorData.value?.length || 0;

    return {
        numSensors,
        sensors: Array.from({ length: numSensors }, (_, i) => ({
            SAID: i,
            value: sensorData.value?.[i],
            min: sensorData.min?.[i],
            max: sensorData.max?.[i]
        })),
        selectedSAID: sensorSAID === undefined
            ? null
            : {
                SAID: sensorSAID,
                value: sensorData.value?.[sensorSAID],
                min: sensorData.min?.[sensorSAID],
                max: sensorData.max?.[sensorSAID]
            }
    };
};

const SensorDetails = (): JSX.Element => {
    const { id, sensorId } = useParams<{ id: string; sensorId: string }>();
    const history = useHistory();
    const location = useLocation();
    const { storeData } = useContext(AppContext);
    const { isDefault, owner } = storeData.organization.currentOrgInfo;
    const [sensor, setSensor] = useState<Sensor | null>(null);
    const [sensorName, setSensorName] = useState("");
    const [isSensorNameEditing, setIsSensorNameEditing] = useState(false);
    const canEditSensorName = false;
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);

    // Configuration form state
    const [configureModalShow, setConfigureModalShow] = useState(false);
    const [showConfirmNoAlertMethod, setShowConfirmNoAlertMethod] = useState(false);
    const [alertConfig, setAlertConfig] = useState<AlertConfig>(getDefaultAlertConfig());
    const [alertUUID, setAlertUUID] = useState("");
    const [sensorMode, setSensorMode] = useState(SensorThreshold.SINGLE_THRESHOLD);
    const [singleThresholdValue, setSingleThresholdValue] = useState("0");
    const [binaryValue, setBinaryValue] = useState("1");
    const [singleActivateCondition, setSingleActivateCondition] = useState(Operator.GREATER_THAN);
    const [dualMinValue, setDualMinValue] = useState("0");
    const [dualMaxValue, setDualMaxValue] = useState("0");
    const [dualActivateCondition, setDualActivateCondition] = useState(Operator.OUT_OF_RANGE.toString());
    const [alertMode, setAlertMode] = useState(SensorAlertMode.ALERT_ONCE);
    const [alertPeriod, setAlertPeriod] = useState(30);
    const [activateMessageContent, setActivateMessageContent] = useState(SensorDefaultMessage.activationMessage);
    const [activateHeaderContent, setActivateHeaderContent] = useState(SensorDefaultMessage.activationHeader);
    const [deactivateMessageContent, setDeactivateMessageContent] = useState(SensorDefaultMessage.deactivationMessage);
    const [deactivateHeaderContent, setDeactivateHeaderContent] = useState(SensorDefaultMessage.deactivationHeader);
    const [activationCheckboxState, setActivationCheckboxState] = useState(false);
    const [deactivationCheckboxState, setDeactivationCheckboxState] = useState(false);
    const [isAlertDisabled, setIsAlertDisabled] = useState(false);
    const [thresholdError, setThresholdError] = useState(false);
    const [thresholdErrorMessage, setThresholdErrorMessage] = useState("");
    const [periodError, setPeriodError] = useState(false);
    const [periodErrorMessage, setPeriodErrorMessage] = useState("");
    const [currEnabledStatus, setCurrEnabledStatus] = useState(false);
    const [initialMemberList, setInitialMemberList] = useState<Array<MemberDto>>([]);
    const [roomMembership, setRoomMembership] = useState<string[]>([]);
    const [zoneMembership, setZoneMembership] = useState<string[]>([]);
    const [groupMembership, setGroupMembership] = useState<string[]>([]);
    const [networkName, setNetworkName] = useState<string>(DEFAULT_VALUES.NETWORK_NAME);
    const [sensorJsonInfo, setSensorJsonInfo] = useState<any>(undefined);
    const [opMode, setOpMode] = useState(0);

    const { currentOrgId } = useOrganisation();

    const READING2_TIME_RANGE = "1d";

    const toNullableNumber = (candidate: any): number | null => {
        if (candidate === "-" || candidate == null) return null;
        const n = Number(candidate);
        return Number.isNaN(n) ? null : n;
    };

    // Constants (destructured for easier access)
    const { ALERT_ONCE, ALERT_CONTINUOUSLY } = SensorAlertMode;
    const { OUT_OF_RANGE, WITHIN_RANGE, LESS_THAN, GREATER_THAN, EQUAL_TO } = Operator;

    // Update isAlertDisabled based on activation/deactivation checkbox state
    // Alert config should be disabled if neither activation nor deactivation checkboxes are checked
    useEffect(() => {
        setIsAlertDisabled(!activationCheckboxState && !deactivationCheckboxState);
    }, [activationCheckboxState, deactivationCheckboxState]);

    const tagHasSensorUID = (tag: any, sensorUID: string, sensorSAID?: number | null) => {
        if (!tag.devices || !Array.isArray(tag.devices)) {
            return false;
        }

        return tag.devices.some((device: any) => {
            const uid = getTagDeviceUID(device);
            if (!uid || String(uid) !== String(sensorUID)) {
                return false;
            }
            if (sensorSAID == null) {
                return true;
            }
            const said = getTagDeviceSAID(device);
            return said === sensorSAID;
        });
    };

    const updateMembershipFromTagValue = (
        tagValue: string,
        rooms: string[],
        zones: string[],
        groups: string[],
        currentHub: string,
    ): string => {
        if (tagValue.startsWith("hub/")) {
            return tagValue.replace("hub/", "");
        }

        if (tagValue.startsWith("zone/")) {
            const zoneName = tagValue.replace("zone/", "");
            if (!zones.includes(zoneName)) {
                zones.push(zoneName);
            }
            return currentHub;
        }

        if (tagValue.startsWith("room/")) {
            const roomName = tagValue.replace("room/", "");
            if (!rooms.includes(roomName)) {
                rooms.push(roomName);
            }
            return currentHub;
        }

        if (tagValue.startsWith("group/")) {
            const groupName = tagValue.replace("group/", "");
            if (!groups.includes(groupName)) {
                groups.push(groupName);
            }
        }

        return currentHub;
    };

    /**
     * Fetches hub name, room membership, and group membership using the tags API
     * Tags API provides membership information by checking which tags contain the sensor UID/SAID
     * @param sensorUID - The sensor device UID
     * @param sensorSAID - The sensor SAID (channel)
     * @param gatewayId - The gateway ID (used for fetching network name from registry)
     */
    const fetchSensorMembership = async (sensorUID: string, gatewayId: string, sensorSAID?: number | null) => {
        if (!currentOrgId || !sensorUID) {
            return;
        }

        try {
            // Fetch all tags with relationship using tags API
            // Tags contain devices array with format: [["gatewayId", "UID", SAID], ...]
            const tagsResponse = await AxiosInstance.get("/tags", {
                params: {
                    organisation_id: currentOrgId,
                    with_relationship: true,
                },
            });

            if (!isHttpSuccess(tagsResponse.status)) {
                return;
            }

            const tags = tagsResponse.data?.data || [];
            const rooms: string[] = [];
            const zones: string[] = [];
            const groups: string[] = [];
            let hub: string = DEFAULT_VALUES.HUB_NAME;

            // Find tags that contain this sensor UID/SAID
            // Tag value format: "hub/name", "zone/name", "room/name", "group/name"
            for (const tag of tags as any[]) {
                if (!tagHasSensorUID(tag, sensorUID, sensorSAID)) {
                    continue;
                }

                const tagValue = tag.value || "";
                hub = updateMembershipFromTagValue(tagValue, rooms, zones, groups, hub);
            }

            // Set zone, room and group membership from tags API
            setZoneMembership(zones);
            setRoomMembership(rooms);
            setGroupMembership(groups);

            // Fetch network name from registry
            try {
                const registryRes = await getOneRegistry(gatewayId);
                if (isHttpSuccess(registryRes.status)) {
                    const fetchedNetworkName = registryRes.data?.name || DEFAULT_VALUES.NETWORK_NAME;
                    setNetworkName(fetchedNetworkName);
                }
            } catch {
                // Error fetching network name - non-critical, continue without network name
            }
        } catch {
            // Error fetching sensor membership - non-critical, continue without membership info
        }
    };

    const { validateAndRedirect } = useNetworkIdRedirect(id, false);

    useEffect(() => {
        runSensorDetailsMountEffect({
            validateAndRedirect,
            locationState: location.state as any,
            id,
            sensorId,
            reformattedNumberString: (value: number | string) =>
                reformattedNumberStringRef.current(value),
            fetchSensorMembership,
            setIsPageLoading,
            setSensor,
            setSensorName,
            setSensorMode,
            setBinaryValue,
            setSingleThresholdValue,
            setDualMinValue,
            setDualMaxValue,
            setSingleActivateCondition,
            setDualActivateCondition,
            setAlertMode,
            setAlertPeriod,
            setActivateHeaderContent,
            setActivateMessageContent,
            setDeactivateHeaderContent,
            setDeactivateMessageContent,
            setActivationCheckboxState,
            setDeactivationCheckboxState,
            setInitialMemberList,
            setAlertUUID,
            setCurrEnabledStatus,
            setAlertConfig,
            setSensorJsonInfo,
            setOpMode,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sensorId, location.state, id, history, currentOrgId]);

    const saveSensorName = async () => {
        if (!canEditSensorName) {
            return;
        }
        if (sensorName === sensor?.name) {
            return setIsSensorNameEditing(false);
        }

        if (!sensorName || sensorName.length > 32) {
            setIsSensorNameEditing(true);
            return showErrorAlert({
                message: "Sensor Name must be between 1-32 characters.",
            });
        }

        setIsLoading(true);
        try {
            /* istanbul ignore next -- save control only shown when sensor is loaded */
            if (!sensor) {
                setIsLoading(false);
                return;
            }

            const { status, data = {} } = await updateSensorConfig(
                id,
                sensor.ldsuUuid,
                String(sensor.SAID ?? ""),
                { name: sensorName }
            );

            if (isHttpSuccess(status)) {
                showSuccessAlert({
                    message: "Sensor name has been updated successfully",
                });
                setSensor({
                    ...sensor,
                    name: sensorName,
                });
                setIsSensorNameEditing(false);
            } else if (status === PAYMENT_REQUIRED) {
                useWalletStore.setState?.({
                    modalShow: true,
                });
            } else {
                showErrorAlert({
                    message: get(
                        data,
                        "description",
                        "Something went wrong! Please try again."
                    ),
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const enableSensorNameEditing = () => {
        if (!canEditSensorName) {
            return;
        }
        setIsSensorNameEditing(true);
    };

    const refreshThisSensor = async () => {
        if (!id || !sensor?.ldsuUuid) {
            return;
        }

        setIsRefreshing(true);
        try {
            // Get SAID from sensor object or extract from sensorId
            let sensorSAID = sensor?.SAID || 0;
            if (!sensorSAID && sensorId?.includes("-")) {
                // Extract SAID from sensorId (format: ldsu_uuid-SAID)
                const parts = sensorId.split("-");
                const saidPart = parts.at(-1) ?? "";
                sensorSAID = Number.parseInt(saidPart, 10) || 0;
            }

            // Readings for the selected time window (1 day)
            const { data, status } = await getSensorLastReading(
                id,
                sensor.ldsuUuid,
                READING2_TIME_RANGE
            );

            // Debug sensor data structure if arrays are present
            if (isHttpSuccess(status) && data && Array.isArray(data.value)) {
                debugSensorData({
                    UID: data.UID || sensor.ldsuUuid || "",
                    ts: data.ts || 0,
                    value: data.value,
                    min: data.min || [],
                    max: data.max || []
                }, sensorSAID);
            }

            if (isHttpSuccess(status) && data) {
                // Update sensor readings with real data from API
                // gateway /reading2 returns arrays; index by SAID.
                const updatedReading = {
                    ...sensor.readings,
                    value: toNullableNumber(data.value?.[sensorSAID]),
                    timestamp: toNullableNumber(data.ts),
                };

                setSensor({
                    ...sensor,
                    readings: updatedReading,
                    currentValue: updatedReading.value ?? 0,
                    lastActive:
                        updatedReading.timestamp === null ||
                        updatedReading.timestamp === undefined
                            ? "-"
                            : moment(updatedReading.timestamp, "X").calendar(getCalendarFormat()),
                });
            }
        } catch {
            // Error refreshing sensor reading - non-critical, continue with existing readings
        } finally {
            // Defer clearing so the refresh icon animation state is observable immediately.
            // (Also keeps behavior stable in unit tests where API mocks may resolve/reject synchronously.)
            setTimeout(() => setIsRefreshing(false), 0);
        }
    };

    const renderDateTime = (readings: SensorReading | undefined): string => {
        if (readings) {
            const ts = readings.timestamp;
            return ts === null || ts === undefined
                ? "-"
                : moment(ts, "X").calendar(getCalendarFormat());
        }

        /* istanbul ignore next -- readings always defined when sensor is rendered */
        return "-";
    };

    // Get value from APPLICATION mode, MODE-based values, or direct property
    const getValueModeOrObj = (obj: any, valueName: string, opMode: number) => {
        if (obj?.APPLICATION?.length ?? undefined) {
            const app = obj.APPLICATION.find(({ APPID }: any) => {
                return APPID === sensor?.originalData?.app_id;
            });
            const defaultUnit = getPrimaryUnitName(app);
            return defaultUnit[valueName];
        }

        const modeValue = get(obj, `${valueName}`),
            objValue = get(obj, `MODE`)?.find(
                ({ ID }: { ID: string }) => ID === opMode.toString()
            )?.[valueName];
        return modeValue === undefined ? objValue || "" : modeValue;
    };

    const fallbackAccuracy = Number(sensor?.accuracy ?? 2);
    const configuredAccuracy = getValueModeOrObj(sensorJsonInfo, "ACCURACY", opMode);
    const parsedConfiguredAccuracy = Number(configuredAccuracy);
    const hasConfiguredAccuracy =
        configuredAccuracy !== "" &&
        configuredAccuracy !== null &&
        configuredAccuracy !== undefined;
    const displayAccuracy = hasConfiguredAccuracy && !Number.isNaN(parsedConfiguredAccuracy)
        ? getExactAccuracy(parsedConfiguredAccuracy)
        : getExactAccuracy(fallbackAccuracy);
    const accuracyException = handleAccuracyException(displayAccuracy);
    const formatSummaryReading = (reading: number | null | undefined): string =>
        formatSensorReadingCellValue({
            unit: sensor?.unit,
            accuracy: displayAccuracy,
            readings: { value: reading },
        });
    const min = Number(getValueModeOrObj(sensorJsonInfo, "MIN", opMode));
    const max = Number(getValueModeOrObj(sensorJsonInfo, "MAX", opMode));
    const format = getValueModeOrObj(sensorJsonInfo, "FORMAT", opMode);
    const { min: displayedMin, max: displayedMax } = customMinMax({
        min: Number(min),
        max: Number(max),
        acc: Number(accuracyException),
    });

    const reformattedNumberString = (value: number | string) =>
        formatThresholdValue(value, accuracyException);

    // Mount/load applies alert thresholds asynchronously; the effect closure would otherwise keep the
    // first render's reformattedNumberString (wrong accuracy before device JSON / op mode are loaded).
    const reformattedNumberStringRef = useRef(reformattedNumberString);
    reformattedNumberStringRef.current = reformattedNumberString;

    // Ensure thresholds reflect sensor accuracy (e.g. CO₂ with ACCURACY=0 shows integers)
    useEffect(() => {
        if (!sensor) {
            return;
        }

        // Only normalize when accuracy is explicitly 0 (integer sensors like CO₂)
        if (displayAccuracy !== 0) {
            return;
        }

        const normalize = (prev: string) => {
            if (prev === "" || prev === "-") {
                return prev;
            }
            const n = Number(prev);
            return Number.isNaN(n) ? prev : String(Math.round(n));
        };

        if (sensorMode === SensorThreshold.SINGLE_THRESHOLD) {
            setSingleThresholdValue(normalize);
        } else if (sensorMode === SensorThreshold.DUAL_THRESHOLD) {
            setDualMinValue(normalize);
            setDualMaxValue(normalize);
        }
    }, [displayAccuracy, sensor, sensorMode]);

    const onAlertConfigChange = (config: AlertConfig) => {
        setAlertConfig(config);
    };

    const isAlertEnabled = () =>
        alertConfig.Notification.isEnabled;

    const validateMsgLength = (msg = "", header?: string) => {
        if (
            header !== undefined &&
            (header.length < 1 || header.length > 280)
        ) {
            return `Subject header should contain 1 to 280 characters.`;
        }

        if (msg.length < 1 || msg.length > 280) {
            return `Message should contain 1 to 280 characters.`;
        }
        return "";
    };

    const validateActivationMessage = () => {
        if (!activationCheckboxState) {
            return true;
        }

        const hasHeaderContent = !!activateHeaderContent && activateHeaderContent.trim().length > 0;
        const hasMessageContent = !!activateMessageContent && activateMessageContent.trim().length > 0;

        if (!hasHeaderContent || !hasMessageContent) {
            showErrorAlert({
                message:
                    "Activation subject header and message cannot be empty.",
            });
            return false;
        }

        const errorMessage = validateMsgLength(
            activateMessageContent,
            activateHeaderContent
        );

        if (errorMessage) {
            showErrorAlert({ message: errorMessage });
            return false;
        }

        return true;
    };

    const validateDeactivationMessage = () => {
        if (!deactivationCheckboxState) {
            return true;
        }

        const hasHeaderContent = !!deactivateHeaderContent && deactivateHeaderContent.trim().length > 0;
        const hasMessageContent = !!deactivateMessageContent && deactivateMessageContent.trim().length > 0;

        if (!hasHeaderContent || !hasMessageContent) {
            showErrorAlert({
                message:
                    "Deactivation subject header and message cannot be empty.",
            });
            return false;
        }

        const errorMessage = validateMsgLength(
            deactivateMessageContent,
            deactivateHeaderContent
        );

        if (errorMessage) {
            showErrorAlert({ message: errorMessage });
            return false;
        }

        return true;
    };

    const validateMessageToggles = () => {
        if (activationCheckboxState || deactivationCheckboxState) {
            return true;
        }

        showErrorAlert({
            message:
                "At least one message (activation or deactivation) must be enabled.",
        });
        return false;
    };

    const validateRecipientsList = (notificationList: any[]) => {
        if (notificationList.length > 0) {
            return true;
        }

        showErrorAlert({
            message: "At least one recipient must be selected.",
        });
        return false;
    };

    const validateRecipientsToggleField = () => {
        // For networks, we only support Push Notification
        const {
            Notification: {
                isEnabled: enabledNotification,
                recipients: notificationList,
            },
        } = alertConfig;

        // Always validate subject/message length (1-280) when activation or deactivation is checked,
        // even when Send Notification is OFF, because we still send the notice body to the API.
        if (!validateActivationMessage()) {
            return false;
        }

        if (!validateDeactivationMessage()) {
            return false;
        }

        if (!enabledNotification) {
            return true;
        }

        if (!validateMessageToggles()) {
            return false;
        }

        if (!validateRecipientsList(notificationList)) {
            return false;
        }

        return true;
    };

    const generateMQTTString = (
        editableSensorDetails: any,
        value: string,
        isIOController: boolean
    ) => {
        const metricInfo = LocalStorageService.getItem("metricInfo");

        const { cls, app_id, primaryUnitKey } = editableSensorDetails;

        const filteredObj = isIOController
            ? metricInfo.app[app_id].metrics[primaryUnitKey]
            : metricInfo.cls[cls].metric;

        return filteredObj;
    };

    const getAlertBody = () => {
        /* istanbul ignore next -- originalData is always set from navigation state or API build */
        if (!sensor?.originalData) {
            return null;
        }

        const editableSensorDetails = sensor.originalData;
        const mqttString = `$value(${generateMQTTString(
            editableSensorDetails,
            editableSensorDetails.sensor_type,
            !!editableSensorDetails.app_id
        )})`;
        let vars_: Array<string> = [];
        let ops: Array<string> = [];
        if (sensorMode === SensorThreshold.NO_THRESHOLD) {
            vars_ = [mqttString, String(binaryValue)];
            ops = [EQUAL_TO];
        } else if (sensorMode === SensorThreshold.SINGLE_THRESHOLD) {
            vars_ = [mqttString, reformattedNumberString(singleThresholdValue)];
            ops =
                singleActivateCondition === GREATER_THAN
                    ? [GREATER_THAN]
                    : [LESS_THAN];
        } else if (sensorMode === SensorThreshold.DUAL_THRESHOLD) {
            ops =
                dualActivateCondition.toString() === OUT_OF_RANGE.toString()
                    ? OUT_OF_RANGE
                    : WITHIN_RANGE;

            vars_ = [
                mqttString,
                reformattedNumberString(dualMinValue),
                mqttString,
                reformattedNumberString(dualMaxValue),
            ];
        }

        const repeat =
            alertMode === ALERT_CONTINUOUSLY
                ? alertPeriod
                : AlertPeriod.ALERT_ONCE_MAX;
        const body = {
            for_: 1,
            name: editableSensorDetails.ldsu_name || sensor.ldsuName,
            vars_,
            repeat,
            enabled: isAlertEnabled() ? currEnabledStatus : false,
            ops,
            interval: 5,
        };
        return body;
    };

    const getNoticeBody = () => {
        // Always send subject/text from form state so disabling a checkbox does not
        // wipe stored copy on the server; `enabled` controls which slots are active.
        const subject = [activateHeaderContent, deactivateHeaderContent];
        const text = [activateMessageContent, deactivateMessageContent];

        const {
            Notification: {
                isEnabled: enabledNotification,
                recipients: notificationList,
            },
        } = alertConfig;

        let noticeBody: any = {
            enabled_email: false,
            recipients_email: [],
            enabled_sms: false,
            recipients_sms: [],
            enabled_push: enabledNotification,
            recipients_push: extractedIdList(notificationList),
            subject: subject,
            text: text,
            enabled: [activationCheckboxState, deactivationCheckboxState],
        };

        if (isDefault) {
            noticeBody = {
                ...noticeBody,
                recipients_email: [`${owner}`],
                recipients_push: [`${owner}`],
                recipients_sms: [`${owner}`],
            };
        }

        return noticeBody;
    };

    const onChangeActivateCheckbox = () => {
        setActivationCheckboxState(!activationCheckboxState);
    };

    const onChangeDeactivateCheckbox = () => {
        setDeactivationCheckboxState(!deactivationCheckboxState);
    };

    const validateNumber = (value: any) => {
        return Patterns.thresholdPattern.test(value);
    };

    const singleThresholdModeValidation = () => {
        if (
            !validateNumber(singleThresholdValue) ||
            !decimalPlaceMatches(singleThresholdValue, accuracyException)
        ) {
            setThresholdError(true);
            setIsLoading(false);
            setThresholdErrorMessage(
                `Threshold must be a valid number with up to ${accuracyException} d.p.`
            );
            return false;
        }

        const unitSymbol = formatUnitWithSymbol(sensor?.unit || "");
        const unit = unitSymbol ? formatValueWithUnit("", unitSymbol) : "";
        if (Number(singleThresholdValue) > displayedMax) {
            setThresholdError(true);
            setThresholdErrorMessage(
                `Threshold value cannot be higher than the sensor's maximum limit of ${displayedMax.toFixed(
                    accuracyException
                )}${unit}.`
            );
            return false;
        }
        if (Number(singleThresholdValue) < displayedMin) {
            setThresholdError(true);
            setThresholdErrorMessage(
                `Threshold value cannot be lower than the sensor's minimum limit of ${displayedMin.toFixed(
                    accuracyException
                )}${unit}.`
            );
            return false;
        }

        return true;
    };

    const dualThresholdModeValidation = () => {
        if (
            !validateNumber(dualMinValue) ||
            !validateNumber(dualMaxValue) ||
            !decimalPlaceMatches(dualMinValue, accuracyException) ||
            !decimalPlaceMatches(dualMaxValue, accuracyException)
        ) {
            setThresholdError(true);
            setIsLoading(false);
            setThresholdErrorMessage(
                `Limit must be a valid number with up to ${accuracyException} d.p.`
            );
            return false;
        }

        const unitSymbol = formatUnitWithSymbol(sensor?.unit || "");
        const unit = unitSymbol ? formatValueWithUnit("", unitSymbol) : "";

        if (Number(dualMinValue) > displayedMax) {
            setThresholdError(true);
            setThresholdErrorMessage(
                `Min limit threshold value cannot be higher than Max limit threshold value of ${displayedMax.toFixed(
                    accuracyException
                )}${unit}.`
            );
            return false;
        }

        if (Number(dualMaxValue) < displayedMin) {
            setThresholdError(true);
            setThresholdErrorMessage(
                `Max limit threshold value cannot be lower than Min limit threshold value of ${displayedMin.toFixed(
                    accuracyException
                )}${unit}.`
            );
            return false;
        }

        if (Number(dualMinValue) < displayedMin) {
            setThresholdError(true);
            setThresholdErrorMessage(
                `Min limit threshold value cannot be lower than the sensor's minimum limit of ${displayedMin.toFixed(
                    accuracyException
                )}${unit}.`
            );
            return false;
        }
        if (Number(dualMaxValue) > displayedMax) {
            setThresholdError(true);
            setThresholdErrorMessage(
                `Max limit threshold value cannot be higher than the sensor's maximum limit of ${displayedMax.toFixed(
                    accuracyException
                )}${unit}.`
            );
            return false;
        }

        if (Number(dualMinValue) >= Number(dualMaxValue)) {
            setThresholdError(true);
            setThresholdErrorMessage(
                `Max limit threshold value must be higher than Min limit threshold value`
            );
            return false;
        }
        return true;
    };

    const alertPeriodValidate = (alertPeriod_: number) => {
        if (
            alertMode === ALERT_CONTINUOUSLY &&
            (alertPeriod_ < AlertPeriod.ALERT_CONTINUOUSLY_MIN ||
                alertPeriod_ > AlertPeriod.ALERT_CONTINUOUSLY_MAX)
        ) {
            setPeriodError(true);
            setPeriodErrorMessage(
                `Alert period must be between ${AlertPeriod.ALERT_CONTINUOUSLY_MIN} and ${AlertPeriod.ALERT_CONTINUOUSLY_MAX} seconds.`
            );
            return false;
        }
        setPeriodError(false);
        setPeriodErrorMessage("");
        return true;
    };

    const validateThresholdValues = () => {
        if (sensorMode === SensorThreshold.SINGLE_THRESHOLD) {
            return singleThresholdModeValidation();
        } else if (sensorMode === SensorThreshold.DUAL_THRESHOLD) {
            return dualThresholdModeValidation();
        }
        return true;
    };

    const hasValidSensorInfo = () =>
        !!id && !!sensor?.ldsuUuid && sensor?.SAID !== undefined;

    const resetAlertErrors = () => {
        setThresholdError(false);
        setThresholdErrorMessage("");
        setPeriodError(false);
        setPeriodErrorMessage("");
    };

    const closeAlertModals = () => {
        setConfigureModalShow(false);
        setShowConfirmNoAlertMethod(false);
    };

    const applyAlertEnableOverride = (
        body: any,
        isAlertEnable: boolean | undefined,
    ) => (isAlertEnable === undefined ? body : { ...body, enabled: isAlertEnable });

    const handlePaymentRequired = (statuses: number[]) => {
        const hasPaymentRequired = statuses.includes(PAYMENT_REQUIRED);

        if (hasPaymentRequired) {
            useWalletStore.setState?.({
                modalShow: true,
            });
        }

        return hasPaymentRequired;
    };

    const handleAlertSaveErrors = (
        alertResponse: any,
        noticeResponse: ResponseDto<AlertNoticePatch> | undefined,
    ) => {
        if (
            noticeResponse !== undefined &&
            !isHttpSuccess(noticeResponse.status)
        ) {
            showErrorAlert(
                getAPIError(noticeResponse ?? {}, "Unable to configure. Try again")
            );
            return false;
        }

        if (
            alertResponse !== undefined &&
            !isHttpSuccess(alertResponse.status)
        ) {
            showErrorAlert(
                getAPIError(alertResponse ?? {}, "Unable to configure. Try again")
            );
            return false;
        }

        return true;
    };

    const notifyAlertStatusChange = (alertBody: any) => {
        const shouldShowDisabledInfo =
            !isAlertEnabled() && !alertBody.enabled && currEnabledStatus;

        const shouldShowEnabledInfo =
            alertBody.enabled && !currEnabledStatus;

        if (shouldShowDisabledInfo) {
            showInfoAlert({
                message:
                    "Sensor alert has been disabled since no alert method (Push Notification) is selected.",
            });
            return;
        }

        if (shouldShowEnabledInfo) {
            showSuccessAlert({
                message: `Alert has been enabled. It will take up to 2 minutes for the operation to take effect.`,
            });
        }
    };

    const updateExistingAlertAndNotice = async (
        alertBody: any,
        thisUUID: string,
        isAlertEnable: boolean | undefined,
    ) => {
        /* istanbul ignore next -- guarded by hasValidSensorInfo() before save */
        if (sensor?.SAID === undefined) {
            throw new Error("Sensor information is missing");
        }

        // Match gateways flow:
        // - omit "enabled" when status is unchanged or no explicit override provided
        // - patch notice first; patch alert only if notice patch succeeds
        const alertPatchPayload =
            alertBody.enabled === currEnabledStatus || isAlertEnable === undefined
                ? (({ enabled: _omit, ...rest }) => rest)(alertBody)
                : alertBody;

        let noticeResponse: ResponseDto<AlertNoticePatch> | undefined = undefined;

        noticeResponse = await updateSensorNotice(
            id,
            sensor.ldsuUuid,
            sensor.SAID.toString(),
            thisUUID,
            getNoticeBody()
        );

        let alertResponse: any = undefined;
        if (isHttpSuccess(noticeResponse.status)) {
            alertResponse = await updateAlertNotification(
                id,
                sensor.ldsuUuid,
                sensor.SAID.toString(),
                alertUUID,
                alertPatchPayload
            );
        }

        return { alertResponse, noticeResponse };
    };

    const createAlertAndNotice = async (
        alertBody: any,
        thisUUID: string,
    ) => {
        /* istanbul ignore next -- guarded by hasValidSensorInfo() before save */
        if (sensor?.SAID === undefined) {
            throw new Error("Sensor information is missing");
        }

        let localUUID = thisUUID;
        const alertResponse = await createNewAlertNotification(
            id,
            sensor.ldsuUuid,
            sensor.SAID.toString(),
            alertBody
        );

        if (isHttpSuccess(alertResponse.status)) {
            localUUID = alertResponse.data.uuid;
            setAlertUUID(localUUID);
        }

        const noticeResponse = await updateSensorNotice(
            id,
            sensor.ldsuUuid,
            sensor.SAID.toString(),
            localUUID,
            getNoticeBody()
        );

        return { alertResponse, noticeResponse };
    };

    const saveAlertConfig = async (isAlertEnable: boolean | undefined = undefined) => {
        // Align with gateways: reset UI state first
        resetAlertErrors();
        closeAlertModals();

        if (!hasValidSensorInfo()) {
            showErrorAlert({
                message: "Missing required sensor information.",
            });
            return;
        }

        // Run validation before API for all entry paths (main SAVE + modals)
        if (!alertPeriodValidate(alertPeriod)) return;
        if (!validateRecipientsToggleField()) return;
        if (!validateThresholdValues()) return;

        let alertBody = getAlertBody();

        /* istanbul ignore next -- defensive; getAlertBody is null only without originalData, which all load paths set */
        if (!alertBody) {
            showErrorAlert({
                message: "Unable to generate alert configuration.",
            });
            return;
        }

        setIsLoading(true);
        let thisUUID = alertUUID ?? "";

        alertBody = applyAlertEnableOverride(alertBody, isAlertEnable);

        let alertResponse;
        let noticeResponse: ResponseDto<AlertNoticePatch> | undefined = undefined;

        try {
            if (alertUUID) {
                ({
                    alertResponse,
                    noticeResponse,
                } = await updateExistingAlertAndNotice(alertBody, thisUUID, isAlertEnable));
            } else {
                ({
                    alertResponse,
                    noticeResponse,
                } = await createAlertAndNotice(alertBody, thisUUID));
            }

            setIsLoading(false);

            const alertStatus = alertResponse?.status ?? 0;
            const noticeStatus = noticeResponse?.status ?? 0;

            if (handlePaymentRequired([alertStatus, noticeStatus])) {
                return;
            }

            if (!handleAlertSaveErrors(alertResponse, noticeResponse)) {
                return;
            }

            showSuccessAlert({
                message: `Configuration saved successfully. Please note that you can only update the configuration after 2 minutes.`,
            });

            notifyAlertStatusChange(alertBody);

            history.push(`/network-details/${id}`);
        } catch {
            setIsLoading(false);
            resetAlertErrors();
            closeAlertModals();
            showErrorAlert({
                message: "An error occurred while saving the configuration.",
            });
        }
    };

    const saveAlertConfigValidation = async () => {
        if (!alertPeriodValidate(alertPeriod)) return;
        if (!validateRecipientsToggleField()) return;
        if (!validateThresholdValues()) return;

        if (!isAlertDisabled && !isAlertEnabled()) {
            setShowConfirmNoAlertMethod(true);
        } else if (isAlertEnabled() && !currEnabledStatus) {
            setConfigureModalShow(true);
        } else {
            saveAlertConfig();
        }
    };

    // Render functions for sensor configuration
    const renderNonBinarySensors = () => {
        return (
            <>
                <Form.Label className="mt-3 mb-3">Mode</Form.Label>
                <Form.Row className="sensor-mode">
                    <Col md={3}>
                        <Form.Check
                            name="mode"
                            id={"single"}
                            custom
                            type="radio"
                            value={SensorThreshold.SINGLE_THRESHOLD}
                            checked={
                                sensorMode === SensorThreshold.SINGLE_THRESHOLD
                            }
                            onChange={(e: any) => {
                                setSensorMode(e.target.value);
                                setThresholdError(false);
                            }}
                            label={
                                <span className="ml-2">Single Threshold</span>
                            }
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Check
                            name="mode"
                            id={"dual"}
                            custom
                            type="radio"
                            value={SensorThreshold.DUAL_THRESHOLD}
                            checked={
                                sensorMode === SensorThreshold.DUAL_THRESHOLD
                            }
                            onChange={(e: any) => {
                                setSensorMode(e.target.value);
                                setThresholdError(false);
                            }}
                            label={<span className="ml-2">Dual Threshold</span>}
                        />
                    </Col>
                </Form.Row>

                {sensorMode === SensorThreshold.SINGLE_THRESHOLD && (
                    <React.Fragment>
                        <Form.Row className="mt-4 mb-3">
                            <Col md={2}>
                                <Form.Label>Threshold</Form.Label>
                            </Col>
                            <Col md={10}>
                                <FormDeviceValueControl
                                    value={singleThresholdValue}
                                    {...{
                                        format,
                                        accuracy: accuracyException,
                                        min,
                                        max,
                                    }}
                                    aria-label="threshold"
                                    onChange={(e) => {
                                        setSingleThresholdValue(e.target.value);
                                    }}
                                />
                            </Col>
                        </Form.Row>
                        <Form.Row>
                            <Col md={2}>
                                <Form.Label>Activate when</Form.Label>
                            </Col>
                            <Col md={10}>
                                <Form.Control
                                    as="select"
                                    custom
                                    value={singleActivateCondition}
                                    onChange={(e: any) => {
                                        setSingleActivateCondition(
                                            e.target.value
                                        );
                                    }}
                                >
                                    <option value={GREATER_THAN}>
                                        Greater than
                                    </option>
                                    <option value={LESS_THAN}>Less than</option>
                                </Form.Control>
                            </Col>
                        </Form.Row>
                    </React.Fragment>
                )}

                {sensorMode === SensorThreshold.DUAL_THRESHOLD && (
                    <>
                        <Form.Row className="mt-4 mb-3">
                            <Col md={2}>
                                <Form.Label>Minimum Limit</Form.Label>
                            </Col>
                            <Col md={10}>
                                <FormDeviceValueControl
                                    {...{
                                        format,
                                        accuracy: accuracyException,
                                        min,
                                        max,
                                    }}
                                    aria-label="min-limit"
                                    value={dualMinValue}
                                    onChange={(e) => {
                                        setDualMinValue(e.target.value);
                                    }}
                                />
                            </Col>
                        </Form.Row>
                        <Form.Row className="mb-3">
                            <Col md={2}>
                                <Form.Label>Maximum Limit</Form.Label>
                            </Col>
                            <Col md={10}>
                                <FormDeviceValueControl
                                    {...{
                                        format,
                                        accuracy: accuracyException,
                                        min,
                                        max,
                                    }}
                                    aria-label="max-limit"
                                    value={dualMaxValue}
                                    onChange={(e) => {
                                        setDualMaxValue(e.target.value);
                                    }}
                                />
                            </Col>
                        </Form.Row>
                        <Form.Row className="mb-3">
                            <Col md={2}>
                                <Form.Label>Activate when</Form.Label>
                            </Col>
                            <Col md={10}>
                                <Form.Control
                                    as="select"
                                    custom
                                    value={dualActivateCondition}
                                    onChange={(e: any) => {
                                        setDualActivateCondition(
                                            e.target.value.split(",")
                                        );
                                    }}
                                >
                                    <option value={OUT_OF_RANGE}>
                                        Out of range
                                    </option>
                                    <option value={WITHIN_RANGE}>
                                        Within range
                                    </option>
                                </Form.Control>
                            </Col>
                        </Form.Row>
                    </>
                )}

                {thresholdError && (
                    <p role="alert" className="text-danger">
                        {thresholdErrorMessage}
                    </p>
                )}
            </>
        );
    };

    const isSensorNameReadOnly = !isSensorNameEditing;

    const renderBinarySensors = () => {
        return (
            <Form.Row>
                <Col md={2}>
                    <Form.Label>Activate when</Form.Label>
                </Col>
                <Col md={10}>
                    <Form.Control
                        as="select"
                        custom
                        aria-label="binary"
                        value={binaryValue}
                        onChange={(e: any) => {
                            setBinaryValue(e.target.value);
                        }}
                    >
                        <option value="0">0</option>
                        <option value="1">1</option>
                    </Form.Control>
                </Col>
            </Form.Row>
        );
    };

    if (!sensor) {
        return (
            <ContentWrapper isLoading={isPageLoading}>
                <div className="page-content">
                    <Container fluid>
                        <Row>
                            <Col sm={12}>
                                <h5 className="page-title">Sensor Details</h5>
                                <DynamicBreadcrumb currentPageLabel={BREADCRUMB_LABELS["sensor-details"]} />
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={8}>
                                <div
                                    className="sensor-not-found-panel"
                                    style={{
                                        backgroundColor: STYLE_CONSTANTS.PANEL_BACKGROUND,
                                        borderRadius: STYLE_CONSTANTS.PANEL_BORDER_RADIUS,
                                        padding: STYLE_CONSTANTS.PANEL_PADDING,
                                        marginBottom: STYLE_CONSTANTS.PANEL_MARGIN_BOTTOM
                                    }}
                                >
                                    <p>Sensor not found or loading...</p>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ContentWrapper>
        );
    }

    return (
        <ContentWrapper isLoading={isPageLoading}>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col sm={12} className="sensor-header">
                            <h5 className="page-title">
                                {sensor.name}
                            </h5>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12">
                            <DynamicBreadcrumb currentPageLabel={BREADCRUMB_LABELS["sensor-details"]} />
                        </Col>
                    </Row>
                    <Row className="flex-row-reverse">
                        <Col lg={4} xs={12}>
                            <div className="sensor-summary d-flex justify-content-between">
                                <div className="sensor-info">
                                    <p className="w-100 mt-0 ml-0">
                                        {sensor.sensor_type}
                                    </p>
                                    <div className="w-100 d-inline-block">
                                        <h2 className="d-inline-block mr-3">
                                            {sensor.readings?.value == null
                                                ? "-"
                                                : formatSummaryReading(sensor.readings.value)}
                                        </h2>
                                    </div>
                                </div>
                                <div className="last-active float-right text-right mt-3">
                                    <p>Last active</p>
                                    <p>
                                        <span>
                                            <IconButton
                                                disableRipple
                                                className={
                                                    isRefreshing
                                                        ? "mr-2 bigRefresh refresh-animation"
                                                        : "mr-2 bigRefresh"
                                                }
                                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                                aria-label="Refresh sensor reading"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    refreshThisSensor();
                                                }}
                                                onKeyDown={(e) => {
                                                    e.stopPropagation();
                                                    refreshThisSensor();
                                                }}
                                            >
                                                <img
                                                    src={refreshIcon}
                                                    alt="refresh"
                                                />
                                            </IconButton>
                                            <span className="last-active-date-time">
                                                {renderDateTime(sensor.readings)}
                                            </span>
                                        </span>
                                    </p>
                                </div>
                            </div>
                            <div className="form-box mt-2">
                                <Form.Row>
                                    <Col md="12">
                                        <Form.Label>Sensor Name</Form.Label>
                                    </Col>
                                    <Col lg={8} xs={12} className="mb-2">
                                        <Form.Group className="mb-0">
                                            <Form.Control
                                                type="text"
                                                required
                                                aria-label="name"
                                                disabled={isSensorNameReadOnly}
                                                value={sensorName}
                                                onChange={(e) => {
                                                    setSensorName(e.target.value);
                                                }}
                                                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        saveSensorName();
                                                    }
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col lg={4} xs={12}>
                                        {isSensorNameEditing ? (
                                            <Button
                                                type="submit"
                                                className="w-100"
                                                onClick={saveSensorName}
                                                disabled={isLoading}
                                            >
                                                {isLoading ? "Saving..." : "Save"}
                                            </Button>
                                        ) : (
                                            <Button
                                                    type="button"
                                                    className="w-100"
                                                    onClick={enableSensorNameEditing}
                                                    disabled={!canEditSensorName}
                                                >
                                                    Edit
                                                </Button>
                                        )}
                                    </Col>
                                </Form.Row>
                            </div>
                            <div className="form-box mt-2 pb-5">
                                <Form.Row>
                                    <Col md="12">
                                        <div className="descriptor-item">
                                            <h6 className="float-left">Type</h6>
                                            <h6 className="ginfo float-right">
                                                {sensor.sensor_type}
                                            </h6>
                                        </div>
                                        <div className="descriptor-item">
                                            <h6 className="float-left">Auxiliary Hub Name</h6>
                                            <h6 className="ginfo float-right">
                                                {sensor.ldsuName}
                                            </h6>
                                        </div>
                                        <div className="descriptor-item">
                                            <h6 className="float-left">Auxiliary Hub UUID</h6>
                                            <h6 className="ginfo float-right">
                                                {sensor.ldsuUuid}
                                            </h6>
                                        </div>

                                        <div className="descriptor-item">
                                            <h6 className="float-left">Network Name</h6>
                                            <h6 className="ginfo float-right">
                                                {networkName}
                                            </h6>
                                        </div>
                                        <div className="descriptor-item">
                                            <h6 className="float-left">
                                                {zoneMembership.length > 0
                                                    ? "Zone membership"
                                                    : "Room membership"}
                                            </h6>
                                            <h6 className="ginfo float-right">
                                                {(() => {
                                                    if (zoneMembership.length > 0) {
                                                        return zoneMembership.join(", ");
                                                    }
                                                    if (roomMembership.length > 0) {
                                                        return roomMembership.join(", ");
                                                    }
                                                    return DEFAULT_VALUES.ROOM_MEMBERSHIP;
                                                })()}
                                            </h6>
                                        </div>
                                        <div className="descriptor-item">
                                            <h6 className="float-left">Groups membership</h6>
                                            <h6 className="ginfo float-right">
                                                {groupMembership.length > 0
                                                    ? groupMembership.join(", ")
                                                    : DEFAULT_VALUES.ROOM_MEMBERSHIP}
                                            </h6>
                                        </div>
                                    </Col>
                                </Form.Row>
                            </div>
                            <div className="form-box download-sensor-data mt-2 pb-5">
                                <h5>Download sensor data</h5>
                                <DownloadDataUI
                                    gatewayId={id}
                                    ldsuId={sensor?.ldsuUuid}
                                    said={sensor?.SAID}
                                    downloadType="downloadSensor"
                                    emailLabel="Recieve Link"
                                />
                            </div>
                        </Col>
                        <Col lg={8} xs={12}>
                            <div className="form-box-head pb-4">
                                <h5 className="float-left">
                                    Configuration
                                </h5>
                            </div>
                            <div className="form-box">
                                {(() => {
                                    // Use local format variable (correctly extracted with getValueModeOrObj) 
                                    // or fallback to sensor.format
                                    const sensorFormat = format || sensor?.format;
                                    return sensorFormat === SensorDataType.Boolean
                                        ? renderBinarySensors()
                                        : renderNonBinarySensors();
                                })()}

                                <hr />

                                <Form.Label className="mt-3 mb-3">
                                    Activation Alert Options{" "}
                                </Form.Label>
                                <Form.Row className="alert-once-continuous">
                                    <Col md={2}>
                                        <Form.Check
                                            name="alert"
                                            id={"once"}
                                            custom
                                            type="radio"
                                            value={ALERT_ONCE}
                                            checked={alertMode === ALERT_ONCE}
                                            onChange={(e: any) => setAlertMode(e.target.value)}
                                            label={<span className="ml-2">Once</span>}
                                        />
                                    </Col>
                                    <Col md={2}>
                                        <Form.Check
                                            name="alert"
                                            id={"continuously"}
                                            custom
                                            type="radio"
                                            value={ALERT_CONTINUOUSLY}
                                            checked={alertMode === ALERT_CONTINUOUSLY}
                                            onChange={(e: any) => setAlertMode(e.target.value)}
                                            label={<span className="ml-2">Continuously</span>}
                                        />
                                    </Col>
                                </Form.Row>

                                {alertMode === ALERT_CONTINUOUSLY && (
                                    <Form.Row className="mt-4 mb-4">
                                        <Col md={2}>
                                            <Form.Label>Send Activation Alert Every</Form.Label>
                                        </Col>
                                        <Col md={10}>
                                            <Row>
                                                <Col xs={8}>
                                                    <FormControl
                                                        type="number"
                                                        value={alertPeriod}
                                                        onChange={(e) => {
                                                            setAlertPeriod(Number(e.target.value));
                                                            alertPeriodValidate(Number(e.target.value));
                                                        }}
                                                    />
                                                </Col>
                                                <Col xs={4}>Second(s)</Col>
                                            </Row>
                                            {periodError && (
                                                <p className="text-danger">{periodErrorMessage}</p>
                                            )}
                                        </Col>
                                    </Form.Row>
                                )}

                                <hr />
                                <Form.Label className="mt-3 mb-3">Message</Form.Label>
                                <Form.Row>
                                    <Col md xs={12} className="mb-4">
                                        <Form.Check
                                            id={"activation"}
                                            custom
                                            type="checkbox"
                                            checked={activationCheckboxState}
                                            onChange={onChangeActivateCheckbox}
                                            label={<span className="ml-2">Message on activation</span>}
                                            className="mb-3"
                                        />
                                        <Form.Control
                                            className="mb-2"
                                            type="text"
                                            placeholder="Type subject header here"
                                            value={activateHeaderContent}
                                            onChange={(e) => setActivateHeaderContent(e.target.value)}
                                        />
                                        <Form.Control
                                            as="textarea"
                                            placeholder="Type message here"
                                            rows={4}
                                            value={activateMessageContent}
                                            onChange={(e) => setActivateMessageContent(e.target.value)}
                                        />
                                    </Col>
                                    <Col md xs={12} className="mb-4">
                                        <Form.Check
                                            id={"deactivation"}
                                            custom
                                            type="checkbox"
                                            checked={deactivationCheckboxState}
                                            onChange={onChangeDeactivateCheckbox}
                                            label={<span className="ml-2">Message on deactivation</span>}
                                            className="mb-3"
                                        />
                                        <Form.Control
                                            className="mb-2"
                                            type="text"
                                            placeholder="Type subject header here"
                                            value={deactivateHeaderContent}
                                            onChange={(e) => setDeactivateHeaderContent(e.target.value)}
                                        />
                                        <Form.Control
                                            as="textarea"
                                            placeholder="Type message here"
                                            rows={4}
                                            value={deactivateMessageContent}
                                            onChange={(e) => setDeactivateMessageContent(e.target.value)}
                                        />
                                    </Col>
                                </Form.Row>

                                <hr />

                                {alertConfig && (
                                    <>
                                        <WalletAlertComponent showWarningWhen="LowBalance" />
                                        <AlertNoticeConfig
                                            config={alertConfig}
                                            initialRecipients={initialMemberList}
                                            onChange={onAlertConfigChange}
                                            disabled={isAlertDisabled}
                                            showOnlyNotification={true}
                                        />
                                    </>
                                )}
                            </div>
                            <div className="form-box-footer">
                                <HoverAuthorizeTooltip
                                    permission="gateway:update"
                                    className="float-left"
                                >
                                    <Button
                                        className="save-btn"
                                        variant="primary"
                                        onClick={saveAlertConfigValidation}
                                        disabled={alertMode === ALERT_CONTINUOUSLY && periodError}
                                    >
                                        SAVE
                                    </Button>
                                </HoverAuthorizeTooltip>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
            <DefaultModal
                modalShow={showConfirmNoAlertMethod}
                setModalShow={setShowConfirmNoAlertMethod}
                modalType="cancelConfirm"
                modalIcon="help_outline"
                modalContent={
                    "Do you want to save the configuration without enabling any alert methods (Email, SMS, Push Notification)?"
                }
                modalTitle={"No Alert Methods Selected"}
                resendFunction={saveAlertConfig}
            />
            <Modal
                centered
                show={configureModalShow}
                onHide={
                    // istanbul ignore next -- no user-dismiss with backdrop="static" and no header close control
                    () => setConfigureModalShow(false)
                }
                backdrop="static"
                keyboard={false}
                className="border-0"
            >
                <img
                    className={
                        isLoading === true
                            ? "loader-gif d-block"
                            : "loader-gif d-none"
                    }
                    src={ripple}
                    alt="loading"
                />
                <Modal.Body
                    className={
                        isLoading === true
                            ? "alert-modal text-center d-none"
                            : "alert-modal text-center d-block"
                    }
                >
                    <div>
                        <div className="modal-icon-box">
                            <span className="material-icons">help_outline</span>
                        </div>
                        <h3 className="mb-3">Enable Alert?</h3>
                        <p className="mb-4">
                            Do you want to enable the alert now?
                        </p>

                        <Button
                            variant="secondary"
                            onClick={() => {
                                saveAlertConfig(false);
                            }}
                        >
                            NOT NOW
                        </Button>
                        <Button
                            variant="primary"
                            className="ml-2"
                            onClick={() => {
                                saveAlertConfig(true);
                            }}
                        >
                            ENABLE
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </ContentWrapper>
    );
};

export default SensorDetails; 